import React, { useEffect } from 'react'
import { Search, ChevronRight, Link2, AlertCircle } from 'lucide-react'
import { Shortcode } from '../types'
import { getShortcodes } from '../api'

interface ShortcodeListProps {
  shortcodes: Shortcode[]
  onShortcodeSelect: (shortcode: Shortcode) => void
  selectedShortcode: Shortcode | null
  onShortcodesUpdate: (shortcodes: Shortcode[]) => void
}

const ShortcodeList: React.FC<ShortcodeListProps> = ({
  shortcodes,
  onShortcodeSelect,
  selectedShortcode,
  onShortcodesUpdate
}) => {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string>('')
  const [nextToken, setNextToken] = React.useState<string | null>(null)
  const [isLoadingMore, setIsLoadingMore] = React.useState(false)

  const loadShortcodes = async (token?: string, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
      }
      setError('')
      const result = await getShortcodes(token)
      if (result.success && result.data) {
        const { items, nextToken: newToken } = result.data
        setNextToken(newToken || null)
        if (append) {
          const existingShortcodes = new Set(shortcodes.map(sc => sc.shortcode))
          const merged = [...shortcodes]
          for (const item of items) {
            if (!existingShortcodes.has(item.shortcode)) {
              merged.push(item)
            } else {
              // Replace existing item with the updated data
              const index = merged.findIndex(sc => sc.shortcode === item.shortcode)
              if (index !== -1) {
                merged[index] = item
              }
            }
          }
          onShortcodesUpdate(merged)
        } else {
          onShortcodesUpdate(items)
        }
      } else {
        setError(result.error || 'Failed to load shortcodes')
      }
    } catch (error) {
      console.error('Failed to load shortcodes:', error)
      setError('An unexpected error occurred while loading shortcodes')
    } finally {
      if (append) {
        setIsLoadingMore(false)
      } else {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    loadShortcodes()
  }, [])

  const handleLoadMore = () => {
    if (!nextToken) return
    loadShortcodes(nextToken, true)
  }

  const filteredShortcodes = shortcodes.filter(sc =>
    sc.shortcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sc.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sc.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="card hover-lift">
      {/* Enhanced Search Header */}
      <div className="card-header">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search shortcodes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input pl-11"
          />
        </div>
      </div>

      {/* Enhanced Shortcode List */}
      <div className="overflow-y-auto custom-scrollbar">
        {error ? (
          <div className="p-6 text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 fade-in">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-800 font-medium">Error loading shortcodes</span>
              </div>
              <p className="text-red-700 text-sm mb-3">{error}</p>
              <button
                onClick={() => loadShortcodes()}
                className="btn-secondary text-sm"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : isLoading ? (
          <div className="p-8 text-center">
            <div className="loading-spinner mx-auto mb-3"></div>
            <p className="text-caption text-gray-600">Loading shortcodes...</p>
          </div>
        ) : filteredShortcodes.length === 0 ? (
          <div className="p-8 text-center">
            <Link2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-body text-gray-600">
              {shortcodes.length === 0 ? 'No shortcodes yet' : 'No matching shortcodes'}
            </p>
            <p className="text-caption text-gray-500 mt-1">
              {searchTerm ? 'Try a different search term' : 'Create your first shortcode to get started'}
            </p>
          </div>
        ) : (
          filteredShortcodes.map((shortcode) => (
            <div
              key={shortcode.shortcode}
              onClick={() => onShortcodeSelect(shortcode)}
              className={`p-5 border-b border-gray-100 cursor-pointer transition-all duration-200 hover-lift ${
                selectedShortcode?.shortcode === shortcode.shortcode
                  ? 'bg-blue-50 border-l-4 border-l-blue-500'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="font-mono text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-lg font-medium">
                      {shortcode.shortcode}
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                  <p className="text-body text-gray-800 line-clamp-2 mb-1">
                    {shortcode.description}
                  </p>
                  <p className="text-caption text-gray-500 truncate">
                    {shortcode.url}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}

        {nextToken && !error && (
          <div className="p-4 border-t border-gray-100 text-center bg-white">
            <button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="btn-secondary flex items-center justify-center space-x-2 mx-auto"
            >
              {isLoadingMore ? (
                <div className="loading-spinner h-4 w-4"></div>
              ) : null}
              <span>{isLoadingMore ? 'Loading more...' : 'Load more'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Enhanced Footer */}
      <div className="card-footer">
        <div className="flex justify-between items-center">
          <p className="text-muted">
            {filteredShortcodes.length} of {shortcodes.length}
            {shortcodes.length !== 1 ? ' shortcodes' : ' shortcode'}
            {nextToken ? '+' : ''}
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="text-caption text-blue-600 hover:text-blue-700 transition-colors"
            >
              Clear search
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ShortcodeList